<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <ListPageShell>
    <!-- 页面标题 -->
    <template #header>
      <div class="list-page-panel is-header-minimal mx-6 pt-6">
      <div class="flex items-center gap-3 mb-1">
        <div class="w-1 h-6 bg-primary-600 rounded-full" />
        <h1 class="text-2xl font-bold text-slate-900 tracking-tight">分账账单</h1>
      </div>
      <p class="text-slate-500 text-sm ml-4">
        查看客户账单收款与分账进度，支持按状态、应收日期等多维度筛选。
      </p>
      </div>
    </template>

    <!-- 筛选区域 -->
    <template #filters>
      <PageFilterBar
      body-class="p-5"
      :show-refresh="true"
      :refresh-loading="loading"
      @refresh="fetchBillingList"
      class="mx-6 rounded-2xl shadow-sm border-slate-200/60"
    >
        <div class="w-56">
          <label class="block text-xs font-medium text-slate-500 mb-1.5">关键字搜索</label>
          <div class="relative">
            <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              v-model="searchForm.keyword"
              type="text"
              placeholder="客户名称 / 账单编号"
              class="form-search-input form-control-sm w-full"
            >
          </div>
        </div>

        <div class="w-36">
          <label class="block text-xs font-medium text-slate-500 mb-1.5">客户 ID</label>
          <input
            v-model="searchForm.customerId"
            type="number"
            placeholder="输入ID"
            class="form-input form-control-sm w-full"
          >
        </div>

        <div class="w-36">
          <label class="block text-xs font-medium text-slate-500 mb-1.5">订单 ID</label>
          <input
            v-model="searchForm.orderId"
            type="number"
            placeholder="输入ID"
            class="form-input form-control-sm w-full"
          >
        </div>

        <div class="w-72">
          <label class="block text-xs font-medium text-slate-500 mb-1.5">应收日期范围</label>
          <el-date-picker
            v-model="searchForm.dueDateRange"
            type="daterange"
            range-separator="至"
            start-placeholder="开始"
            end-placeholder="结束"
            format="YYYY-MM-DD"
            value-format="YYYY-MM-DD"
            class="!w-full form-date form-control-sm"
          />
        </div>

      <template #searchActions>
        <button
            type="button"
            class="btn-primary inline-flex items-center gap-2"
            @click="handleSearch"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            搜索
          </button>
          <button
            type="button"
            class="btn-secondary"
            @click="resetSearch"
          >
            重置
          </button>
      </template>

      <template #extra>
        <div class="flex gap-2 flex-wrap border-t border-slate-200/60 pt-4">
        <button
          v-for="tab in statusTypeTabs"
          :key="tab.value"
          @click="handleStatusTabClick(tab.value)"
          :class="[
            'px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-200',
            searchForm.statusType === tab.value
              ? 'bg-primary-100 text-primary-700 shadow-sm'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          ]"
        >
          {{ tab.label }}
        </button>
        </div>
      </template>
      </PageFilterBar>
    </template>

    <!-- 卡片列表区域 -->
    <div class="mb-6 mx-6">
      <!-- 加载骨架 -->
      <div
        v-if="loading"
        class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4"
      >
        <div
          v-for="n in 6"
          :key="n"
          class="bg-white rounded-2xl border border-slate-200/60 p-5 animate-pulse"
        >
          <div class="flex items-start justify-between mb-5">
            <div class="space-y-3">
              <div class="h-4 w-36 bg-slate-200 rounded-lg" />
              <div class="h-3 w-28 bg-slate-100 rounded-lg" />
            </div>
            <div class="h-7 w-24 bg-slate-100 rounded-full" />
          </div>
          <div class="space-y-2.5 mb-5">
            <div class="h-4 w-44 bg-slate-100 rounded-lg" />
            <div class="h-3 w-32 bg-slate-50 rounded-lg" />
          </div>
          <div class="space-y-2">
            <div class="h-3 w-full bg-slate-50 rounded-lg" />
            <div class="h-3 w-4/5 bg-slate-50 rounded-lg" />
          </div>
        </div>
      </div>

      <!-- 空状态 -->
      <div
        v-else-if="billingList.length === 0"
        class="bg-white rounded-2xl border border-dashed border-slate-300 py-16 flex flex-col items-center justify-center"
      >
        <div class="w-20 h-20 rounded-full bg-slate-100 flex items-center justify-center mb-4">
          <svg
            class="w-10 h-10 text-slate-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="1.5"
              d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
            />
          </svg>
        </div>
        <h3 class="text-lg font-medium text-slate-700 mb-1">暂无分账账单</h3>
        <p class="text-sm text-slate-500">调整筛选条件或创建新账单</p>
      </div>

      <!-- 卡片列表 -->
      <div
        v-else
        class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4"
      >
        <div
          v-for="bill in billingList"
          :key="bill.id"
          class="bg-white rounded-2xl border border-slate-200/60 p-5 flex flex-col gap-4 hover:shadow-lg hover:border-slate-300/80 hover:-translate-y-0.5 transition-all duration-200 cursor-pointer group"
          @click="showDetail(bill)"
        >
          <!-- 卡片头部：编号 + 支付状态 -->
          <div class="flex items-start justify-between gap-3">
            <div class="min-w-0 flex-1">
              <div class="flex items-center gap-2.5 mb-1.5">
                <span class="font-mono text-sm font-semibold text-slate-900 truncate">
                  {{ bill.orderNo || '-' }}
                </span>
                <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium shrink-0" :class="getPaymentStatusClass(bill)">
                  <span class="w-1.5 h-1.5 rounded-full mr-1.5" :class="getPaymentStatusDotClass(bill)" />
                  {{ getPaymentStatusLabel(bill) }}
                </span>
              </div>
              <div class="flex flex-wrap items-center gap-1.5 text-xs text-slate-500">
                <span class="bg-slate-100 px-1.5 py-0.5 rounded">
                  #{{ bill.id }}
                </span>
                <span v-if="bill.periodType" class="bg-purple-50 text-purple-700 px-1.5 py-0.5 rounded font-medium">
                  {{ bill.periodType }}
                </span>
                <span v-if="bill.periodIndex" class="bg-primary-50 text-primary-700 px-1.5 py-0.5 rounded font-medium">
                  第{{ bill.periodIndex }}期
                </span>
                <span v-if="bill.billingType" class="bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">
                  {{ bill.billingType }}
                </span>
                <span
                  v-if="bill.isDraft === 1"
                  class="inline-flex items-center px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200 text-xs font-medium"
                >
                  草稿
                </span>
              </div>
            </div>
          </div>

          <!-- 分隔线 -->
          <div class="h-px bg-gradient-to-r from-slate-100 via-slate-200 to-transparent" />

          <!-- 客户 / 金额 -->
          <div class="flex items-start justify-between gap-4">
            <div class="flex-1 min-w-0">
              <div class="text-xs text-slate-500 mb-1 font-medium uppercase tracking-wide">
                客户信息
              </div>
              <div class="text-sm font-semibold text-slate-900 truncate mb-0.5" :title="bill.customerName">
                {{ bill.customerName || '-' }}
              </div>
              <div class="text-xs text-slate-500">
                {{ bill.customerPhone || '暂无手机号' }}
              </div>
              <div class="text-xs text-slate-400 mt-1 truncate" :title="bill.productName">
                {{ bill.productName || '-' }}
              </div>
            </div>

            <div class="w-40 text-right shrink-0">
              <div class="text-xs text-slate-500 mb-1 font-medium uppercase tracking-wide">
                应收金额
              </div>
              <div class="text-xl font-bold text-slate-900">
                {{ formatAmount(bill.amount) }}
              </div>
              <div class="mt-1.5 text-xs text-slate-500">
                已收 {{ formatAmount(bill.paidAmount) }}
              </div>
              <div class="mt-2">
                <div class="flex items-center justify-between text-xs text-slate-400 mb-1.5">
                  <span>回款进度</span>
                  <span class="font-medium text-slate-600">{{ getPaidPercent(bill) }}</span>
                </div>
                <div class="h-2 w-full bg-slate-100 rounded-full overflow-hidden">
                  <div
                    class="h-full bg-gradient-to-r from-emerald-400 to-emerald-500 rounded-full transition-all duration-500"
                    :style="{ width: getPaidPercent(bill) }"
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- 分账与时间信息 -->
          <div class="grid grid-cols-2 gap-4 p-3 bg-slate-50/80 rounded-xl">
            <div>
              <div class="flex items-center gap-1.5 mb-2">
                <svg class="w-3.5 h-3.5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <span class="text-xs font-medium text-slate-500 uppercase tracking-wide">分账状态</span>
              </div>
              <span
                class="inline-flex items-center px-2 py-1 rounded-lg text-xs font-semibold"
                :class="getCommissionStatusClass(bill.commissionStatus)"
              >
                {{ getCommissionStatusLabel(bill.commissionStatus) }}
              </span>
              <div class="mt-2 space-y-0.5 text-xs text-slate-500">
                <div class="flex justify-between">
                  <span>已分账</span>
                  <span class="font-medium text-emerald-600">{{ formatAmount(bill.commissionSettledAmount) }}</span>
                </div>
                <div class="flex justify-between">
                  <span>待分账</span>
                  <span class="font-medium text-amber-600">{{ formatAmount(bill.commissionPendingAmount) }}</span>
                </div>
              </div>
            </div>

            <div>
              <div class="flex items-center gap-1.5 mb-2">
                <svg class="w-3.5 h-3.5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span class="text-xs font-medium text-slate-500 uppercase tracking-wide">时间信息</span>
              </div>
              <div class="space-y-1 text-xs text-slate-500">
                <div class="flex justify-between">
                  <span>应收</span>
                  <span class="font-medium text-slate-700">{{ bill.dueDate || '-' }}</span>
                </div>
                <div class="flex justify-between">
                  <span>收款</span>
                  <span class="font-medium text-slate-700">{{ bill.paidTime || '-' }}</span>
                </div>
                <div class="flex justify-between">
                  <span>创建</span>
                  <span class="font-medium text-slate-700">{{ bill.createTime || '-' }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- 备注与操作 -->
          <div class="flex items-center justify-between gap-3 pt-1">
            <div class="text-xs text-slate-400 truncate max-w-[55%] flex items-center gap-1.5">
              <svg class="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A2 2 0 013 12V7a4 4 0 014-4z" />
              </svg>
              {{ bill.remark || '暂无备注' }}
            </div>
            <div class="flex items-center gap-2">
              <!-- 执行分账按钮：已支付待分账状态（status=1 且 commissionStatus=0或1） -->
              <button
                v-if="canExecuteCommission(bill)"
                class="px-3 py-1.5 text-xs text-white bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 rounded-lg font-medium transition-all flex items-center gap-1.5 shadow-sm shadow-amber-500/20"
                @click.stop="handleExecuteCommission(bill)"
              >
                <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                执行分账
              </button>
              <button
                v-if="bill.status !== 1"
                class="px-3 py-1.5 text-xs text-teal-700 hover:bg-teal-50 disabled:opacity-60 disabled:cursor-not-allowed rounded-lg font-medium transition-colors flex items-center gap-1.5"
                :disabled="creatingCashierBillingId === bill.id"
                @click.stop="openCashierPage(bill)"
              >
                <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 1.12-3 2.5s1.343 2.5 3 2.5 3 1.12 3 2.5S13.657 18 12 18m0-10V6m0 12v-2M5 12a7 7 0 1114 0 7 7 0 01-14 0z" />
                </svg>
                {{ creatingCashierBillingId === bill.id ? '创建中...' : '去收银' }}
              </button>
              <button
                class="px-3 py-1.5 text-xs text-primary-600 hover:bg-primary-50 rounded-lg font-medium transition-colors flex items-center gap-1.5"
                @click.stop="showDetail(bill)"
              >
                <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                详情
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 分页 -->
    <AppPagination
      v-if="pagination.total > 0"
      class="pagination-bridge"
      :pagination="pagination"
      :page-sizes="[9, 18, 27]"
      @page-change="handlePageChange"
      @size-change="handlePageSizeChange"
    />

    <!-- 详情弹窗 -->
    <Teleport to="body">
      <div v-if="showDetailModal" class="fixed inset-0 z-50 overflow-y-auto" @keydown.esc="closeDetail">
        <div class="fixed inset-0 bg-slate-900/60 backdrop-blur-sm animate-fade-in" @click="closeDetail" />
        <div class="relative min-h-screen flex items-center justify-center p-4">
          <div class="relative bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] flex flex-col overflow-hidden animate-slide-up">
            <!-- 弹窗头部 -->
            <div class="flex-shrink-0 flex items-center justify-between px-6 py-4 bg-gradient-to-r from-slate-50 to-white border-b border-slate-100">
              <div class="flex items-center gap-4">
                <div class="w-11 h-11 rounded-xl bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center shadow-lg shadow-primary-500/20">
                  <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                </div>
                <div class="flex flex-col gap-0.5">
                  <div class="flex items-center gap-3">
                    <h3 class="text-lg font-bold text-slate-900">账单详情</h3>
                    <span
                      class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold"
                      :class="getBillingStatusClass(currentBill?.status)"
                    >
                      {{ getBillingStatusLabel(currentBill?.status) }}
                    </span>
                    <span
                      v-if="currentBill?.isDraft === 1"
                      class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200"
                    >
                      草稿
                    </span>
                  </div>
                  <p class="text-sm text-slate-500 font-mono">#{{ currentBill?.id }} · {{ currentBill?.order?.orderNo }}</p>
                </div>
              </div>
              <button class="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors" @click="closeDetail">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div class="flex-1 overflow-y-auto min-h-0 px-6 py-5 space-y-5">
              <!-- 账单状态概览 -->
              <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div class="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 border border-blue-200">
                  <div class="text-xs text-blue-600 mb-1 font-medium uppercase tracking-wide">账单类型</div>
                  <div class="text-sm font-semibold text-blue-900">
                    {{ currentBill?.billingType === 'ONCE' ? '一次性' : currentBill?.billingType === 'PERIODIC' ? '周期性' : '-' }}
                    <span v-if="currentBill?.periodType" class="text-xs text-blue-600 ml-1">
                      ({{ currentBill?.periodType === 'MONTH' ? '月' : currentBill?.periodType === 'QUARTER' ? '季度' : currentBill?.periodType === 'YEAR' ? '年' : '' }})
                    </span>
                  </div>
                  <div v-if="currentBill?.periodIndex" class="text-xs text-blue-600 mt-1">第 {{ currentBill?.periodIndex }} 期</div>
                </div>
                <div class="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4 border border-purple-200">
                  <div class="text-xs text-purple-600 mb-1 font-medium uppercase tracking-wide">应收金额</div>
                  <div class="text-xl font-bold text-purple-900">¥{{ formatAmount(currentBill?.amount) }}</div>
                </div>
                <div class="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl p-4 border border-emerald-200">
                  <div class="text-xs text-emerald-600 mb-1 font-medium uppercase tracking-wide">已收金额</div>
                  <div class="text-xl font-bold text-emerald-900">¥{{ formatAmount(currentBill?.paidAmount) }}</div>
                </div>
                <div class="bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl p-4 border border-amber-200">
                  <div class="text-xs text-amber-600 mb-1 font-medium uppercase tracking-wide">待分账金额</div>
                  <div class="text-xl font-bold text-amber-900">¥{{ formatAmount(currentBill?.commissionPendingAmount) }}</div>
                </div>
              </div>

              <!-- tabs -->
              <div class="border-b border-slate-200">
                <div class="flex gap-4">
                  <button
                    v-for="tab in detailTabs"
                    :key="tab.key"
                    @click="activeDetailTab = tab.key"
                    :class="[
                      'px-1 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px',
                      activeDetailTab === tab.key
                        ? 'text-primary-600 border-primary-600'
                        : 'text-slate-500 border-transparent hover:text-slate-700'
                    ]"
                  >
                    {{ tab.label }}
                  </button>
                </div>
              </div>

              <!-- 基本信息 -->
              <div v-if="activeDetailTab === 'basic'" class="space-y-5">
                <!-- 账单信息 -->
                <div class="bg-slate-50/80 rounded-xl p-4">
                  <h4 class="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                    <span class="w-1.5 h-4 bg-primary-500 rounded-full" />
                    账单信息
                  </h4>
                  <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div class="bg-white rounded-lg p-3 border border-slate-100">
                      <div class="text-xs text-slate-500 mb-1">账单编号</div>
                      <div class="text-sm font-medium text-slate-900 font-mono">{{ currentBill?.order?.orderNo || '-' }}</div>
                    </div>
                    <div class="bg-white rounded-lg p-3 border border-slate-100">
                      <div class="text-xs text-slate-500 mb-1">应收日期</div>
                      <div class="text-sm font-medium text-slate-900">{{ currentBill?.dueDate || '-' }}</div>
                    </div>
                    <div class="bg-white rounded-lg p-3 border border-slate-100">
                      <div class="text-xs text-slate-500 mb-1">收款时间</div>
                      <div class="text-sm font-medium text-slate-900">{{ currentBill?.paidTime || '-' }}</div>
                    </div>
                    <div class="bg-white rounded-lg p-3 border border-slate-100">
                      <div class="text-xs text-slate-500 mb-1">支付渠道</div>
                      <div class="text-sm font-medium text-slate-900">{{ getPayChannelLabel(currentBill?.payChannel) }}</div>
                    </div>
                    <div class="bg-white rounded-lg p-3 border border-slate-100">
                      <div class="text-xs text-slate-500 mb-1">创建人</div>
                      <div class="text-sm font-medium text-slate-900">{{ currentBill?.createBy || '-' }}</div>
                    </div>
                    <div class="bg-white rounded-lg p-3 border border-slate-100">
                      <div class="text-xs text-slate-500 mb-1">创建时间</div>
                      <div class="text-sm font-medium text-slate-900">{{ currentBill?.createTime || '-' }}</div>
                    </div>
                    <div class="bg-white rounded-lg p-3 border border-slate-100">
                      <div class="text-xs text-slate-500 mb-1">更新人</div>
                      <div class="text-sm font-medium text-slate-900">{{ currentBill?.updateBy || '-' }}</div>
                    </div>
                    <div class="bg-white rounded-lg p-3 border border-slate-100">
                      <div class="text-xs text-slate-500 mb-1">更新时间</div>
                      <div class="text-sm font-medium text-slate-900">{{ currentBill?.updateTime || '-' }}</div>
                    </div>
                  </div>
                  <div v-if="currentBill?.remark" class="mt-3 bg-white rounded-lg p-3 border border-slate-100">
                    <div class="text-xs text-slate-500 mb-1">备注</div>
                    <div class="text-sm text-slate-700">{{ currentBill.remark }}</div>
                  </div>
                </div>

                <!-- 支付记录 -->
                <div class="bg-slate-50/80 rounded-xl p-4">
                  <h4 class="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                    <span class="w-1.5 h-4 bg-blue-500 rounded-full" />
                    支付记录
                  </h4>
                  <div v-if="detailLoading" class="flex items-center justify-center py-8">
                    <div class="animate-spin w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full" />
                  </div>
                  <div v-else-if="!detailPayments.length" class="text-center py-6 bg-white rounded-lg border border-slate-100">
                    <div class="text-slate-400 text-sm">暂无支付记录</div>
                  </div>
                  <div v-else class="space-y-3">
                    <div
                      v-for="payment in detailPayments"
                      :key="payment.id"
                      class="bg-white rounded-lg border border-slate-100 overflow-hidden"
                    >
                      <div class="p-3 border-b border-slate-100 bg-gradient-to-r from-blue-50 to-white">
                        <div class="flex items-center justify-between">
                          <div class="flex items-center gap-3">
                            <span class="text-sm font-semibold text-slate-900 font-mono">{{ payment.paymentNo || '-' }}</span>
                            <span
                              class="px-2 py-0.5 rounded text-xs font-medium"
                              :class="getPaymentRecordStatusClass(payment.status)"
                            >
                              {{ getPaymentRecordStatusLabel(payment.status) }}
                            </span>
                          </div>
                          <div class="text-right">
                            <div class="text-sm font-bold text-emerald-600">¥{{ formatAmount(payment.amount) }}</div>
                            <div class="text-xs text-slate-500">{{ payment.paidTime || payment.createTime }}</div>
                          </div>
                        </div>
                      </div>
                      <div class="p-3 grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div class="bg-slate-50 rounded-lg p-2">
                          <div class="text-xs text-slate-500 mb-1">支付渠道</div>
                          <div class="text-sm font-medium text-slate-900">{{ getChannelTypeLabel(payment.channelType) }}</div>
                        </div>
                        <div v-if="payment.channelOrderNo" class="bg-slate-50 rounded-lg p-2">
                          <div class="text-xs text-slate-500 mb-1">渠道订单号</div>
                          <div class="text-sm font-medium text-slate-900 font-mono truncate">{{ payment.channelOrderNo }}</div>
                        </div>
                        <div v-if="payment.payScene" class="bg-slate-50 rounded-lg p-2">
                          <div class="text-xs text-slate-500 mb-1">支付场景</div>
                          <div class="text-sm font-medium text-slate-900">{{ payment.payScene }}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- 分账信息 -->
                <div class="bg-slate-50/80 rounded-xl p-4">
                  <h4 class="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                    <span class="w-1.5 h-4 bg-amber-500 rounded-full" />
                    分账信息
                  </h4>
                  <div class="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
                    <div class="bg-white rounded-lg p-3 border border-slate-100">
                      <div class="text-xs text-slate-500 mb-1">分账状态</div>
                      <span
                        class="inline-flex items-center px-2 py-1 rounded-lg text-xs font-semibold"
                        :class="getCommissionStatusClass(currentBill?.commissionStatus)"
                      >
                        {{ getCommissionStatusLabel(currentBill?.commissionStatus) }}
                      </span>
                    </div>
                    <div class="bg-white rounded-lg p-3 border border-slate-100">
                      <div class="text-xs text-slate-500 mb-1">已分账金额</div>
                      <div class="text-lg font-bold text-emerald-600">¥{{ formatAmount(currentBill?.commissionSettledAmount) }}</div>
                    </div>
                    <div class="bg-white rounded-lg p-3 border border-slate-100">
                      <div class="text-xs text-slate-500 mb-1">待分账金额</div>
                      <div class="text-lg font-bold text-amber-600">¥{{ formatAmount(currentBill?.commissionPendingAmount) }}</div>
                    </div>
                  </div>

                  <!-- 分账记录列表 -->
                  <div v-if="detailLoading" class="flex items-center justify-center py-8">
                    <div class="animate-spin w-6 h-6 border-2 border-amber-500 border-t-transparent rounded-full" />
                  </div>
                  <div v-else-if="!detailCommissions.length" class="text-center py-6 bg-white rounded-lg border border-slate-100">
                    <div class="text-slate-400 text-sm">暂无分账记录</div>
                  </div>
                  <div v-else class="space-y-3">
                    <div
                      v-for="item in detailCommissions"
                      :key="item.settlementId"
                      class="bg-white rounded-lg border border-slate-100 overflow-hidden"
                    >
                      <div class="p-3 border-b border-slate-100 bg-gradient-to-r from-amber-50 to-white">
                        <div class="flex items-center justify-between">
                          <div class="flex items-center gap-3">
                            <span class="text-sm font-semibold text-slate-900 font-mono">{{ item.settlementNo || '-' }}</span>
                            <span
                              class="px-2 py-0.5 rounded text-xs font-medium"
                              :class="getSettlementStatusClass(item.status)"
                            >
                              {{ getSettlementStatusLabel(item.status) }}
                            </span>
                          </div>
                          <div class="text-right">
                            <div class="text-sm font-bold text-amber-600">¥{{ formatAmount(item.totalAmount) }}</div>
                            <div class="text-xs text-slate-500">{{ item.settleTime || item.createTime }}</div>
                          </div>
                        </div>
                        <div class="flex items-center gap-4 mt-2 text-xs text-slate-500">
                          <span>规则：{{ item.ruleName || '-' }}</span>
                          <span>{{ getRuleTypeLabel(item.ruleType) }}</span>
                          <span v-if="item.priority">优先级 {{ item.priority }}</span>
                        </div>
                      </div>
                      <!-- 分账明细 -->
                      <div v-if="item.details?.length" class="p-3">
                        <div class="text-xs font-medium text-slate-500 mb-2">分账明细</div>
                        <div class="space-y-2">
                          <div
                            v-for="(detail, idx) in item.details"
                            :key="idx"
                            class="flex items-center justify-between py-2 border-b border-slate-50 last:border-0"
                          >
                            <div class="flex items-center gap-2">
                              <span class="px-2 py-0.5 bg-primary-50 text-primary-700 rounded text-xs font-medium">
                                {{ getRoleCodeLabel(detail.roleCode) }}
                              </span>
                              <span class="text-sm text-slate-900">{{ detail.roleInstanceName || '-' }}</span>
                              <span
                                class="px-1.5 py-0.5 rounded text-xs"
                                :class="getCommissionItemStatusClass(detail.status)"
                              >
                                {{ getCommissionItemStatusLabel(detail.status) }}
                              </span>
                            </div>
                            <div class="text-right">
                              <div class="text-sm font-bold text-slate-900">¥{{ formatAmount(detail.amount) }}</div>
                              <div class="text-xs text-slate-500">
                                {{ getCalculateTypeLabel(detail.calculateType) }}
                                <span v-if="detail.calculateType === 'RATIO'">{{ detail.ratio }}%</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- 订单信息 -->
              <div v-if="activeDetailTab === 'order'" class="space-y-5">
                <div class="bg-slate-50/80 rounded-xl p-4">
                  <h4 class="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                    <span class="w-1.5 h-4 bg-blue-500 rounded-full" />
                    订单信息
                  </h4>
                  <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div class="bg-white rounded-lg p-3 border border-slate-100">
                      <div class="text-xs text-slate-500 mb-1">订单编号</div>
                      <div class="text-sm font-medium text-slate-900 font-mono">{{ currentBill?.order?.orderNo || '-' }}</div>
                    </div>
                    <div class="bg-white rounded-lg p-3 border border-slate-100">
                      <div class="text-xs text-slate-500 mb-1">订单状态</div>
                      <div class="text-sm font-medium text-slate-900">{{ getOrderStatusLabel(currentBill?.order?.status) }}</div>
                    </div>
                    <div class="bg-white rounded-lg p-3 border border-slate-100">
                      <div class="text-xs text-slate-500 mb-1">订单类型</div>
                      <div class="text-sm font-medium text-slate-900">{{ currentBill?.order?.orderType === 'ONCE' ? '一次性' : currentBill?.order?.orderType === 'PERIODIC' ? '周期性' : '-' }}</div>
                    </div>
                    <div class="bg-white rounded-lg p-3 border border-slate-100">
                      <div class="text-xs text-slate-500 mb-1">分期期数</div>
                      <div class="text-sm font-medium text-slate-900">{{ currentBill?.order?.periodCount ? `${currentBill.order.periodCount} 期` : '-' }}</div>
                    </div>
                    <div class="bg-white rounded-lg p-3 border border-slate-100">
                      <div class="text-xs text-slate-500 mb-1">订单金额</div>
                      <div class="text-sm font-bold text-slate-900">¥{{ formatAmount(currentBill?.order?.amount) }}</div>
                    </div>
                    <div class="bg-white rounded-lg p-3 border border-slate-100">
                      <div class="text-xs text-slate-500 mb-1">服务开始</div>
                      <div class="text-sm font-medium text-slate-900">{{ currentBill?.order?.serviceStart || '-' }}</div>
                    </div>
                    <div class="bg-white rounded-lg p-3 border border-slate-100">
                      <div class="text-xs text-slate-500 mb-1">服务结束</div>
                      <div class="text-sm font-medium text-slate-900">{{ currentBill?.order?.serviceEnd || '-' }}</div>
                    </div>
                    <div class="bg-white rounded-lg p-3 border border-slate-100">
                      <div class="text-xs text-slate-500 mb-1">关联方案</div>
                      <div class="text-sm font-medium text-slate-900">{{ currentBill?.order?.planName || '-' }}</div>
                    </div>
                    <div class="bg-white rounded-lg p-3 border border-slate-100">
                      <div class="text-xs text-slate-500 mb-1">关联企业</div>
                      <div class="text-sm font-medium text-slate-900">{{ currentBill?.order?.companyName || '-' }}</div>
                    </div>
                    <div class="bg-white rounded-lg p-3 border border-slate-100">
                      <div class="text-xs text-slate-500 mb-1">销售人员</div>
                      <div class="text-sm font-medium text-slate-900">{{ currentBill?.order?.ownerUserName || '-' }}</div>
                    </div>
                    <div class="bg-white rounded-lg p-3 border border-slate-100">
                      <div class="text-xs text-slate-500 mb-1">执行人员</div>
                      <div class="text-sm font-medium text-slate-900">{{ currentBill?.order?.executorName || '-' }}</div>
                    </div>
                    <div class="bg-white rounded-lg p-3 border border-slate-100">
                      <div class="text-xs text-slate-500 mb-1">执行进度</div>
                      <div class="text-sm font-medium text-slate-900">{{ currentBill?.order?.progress ?? '-' }}%</div>
                    </div>
                    <div v-if="currentBill?.order?.currentTaskName" class="bg-white rounded-lg p-3 border border-slate-100 col-span-2">
                      <div class="text-xs text-slate-500 mb-1">当前任务</div>
                      <div class="text-sm font-medium text-slate-900">{{ currentBill?.order?.currentTaskName }}</div>
                      <div v-if="currentBill?.order?.currentTaskStatusDesc" class="text-xs text-slate-500 mt-1">{{ currentBill?.order?.currentTaskStatusDesc }}</div>
                    </div>
                  </div>
                </div>

                <!-- 产品信息 -->
                <div class="bg-slate-50/80 rounded-xl p-4">
                  <h4 class="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                    <span class="w-1.5 h-4 bg-purple-500 rounded-full" />
                    产品信息
                  </h4>
                  <div class="bg-white rounded-lg p-4 border border-slate-100 mb-3">
                    <div class="flex items-center gap-3 mb-3">
                      <div v-if="currentBill?.order?.productIcon" class="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                        <img v-if="currentBill.order.productIcon.startsWith('http')" :src="currentBill.order.productIcon" class="w-6 h-6" alt="">
                        <svg v-else class="w-6 h-6 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                        </svg>
                      </div>
                      <div>
                        <div class="text-sm font-semibold text-slate-900">{{ currentBill?.order?.productNameSnapshot || '-' }}</div>
                        <div class="text-xs text-slate-500">{{ currentBill?.order?.productCodeSnapshot || '-' }}</div>
                      </div>
                    </div>
                    <div v-if="currentBill?.order?.productSummarySnapshot" class="mb-3">
                      <div class="text-xs text-slate-500 mb-1">产品摘要</div>
                      <div class="text-sm text-slate-700 whitespace-pre-wrap">{{ currentBill.order.productSummarySnapshot }}</div>
                    </div>
                    <div v-if="currentBill?.order?.serviceScopeSnapshot" class="mb-3">
                      <div class="text-xs text-slate-500 mb-1">服务范围</div>
                      <div class="text-sm text-slate-700 whitespace-pre-wrap">{{ currentBill.order.serviceScopeSnapshot }}</div>
                    </div>
                    <div v-if="currentBill?.order?.deliverablesSnapshot" class="mb-3">
                      <div class="text-xs text-slate-500 mb-1">交付物</div>
                      <div class="text-sm text-slate-700 whitespace-pre-wrap">{{ currentBill.order.deliverablesSnapshot }}</div>
                    </div>
                    <div v-if="currentBill?.order?.noticesSnapshot">
                      <div class="text-xs text-slate-500 mb-1">注意事项</div>
                      <div class="text-sm text-slate-700 whitespace-pre-wrap">{{ currentBill.order.noticesSnapshot }}</div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- 客户信息 -->
              <div v-if="activeDetailTab === 'customer'" class="space-y-5">
                <div class="bg-slate-50/80 rounded-xl p-4">
                  <h4 class="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                    <span class="w-1.5 h-4 bg-emerald-500 rounded-full" />
                    客户信息
                  </h4>
                  <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div class="bg-white rounded-lg p-3 border border-slate-100">
                      <div class="text-xs text-slate-500 mb-1">客户编号</div>
                      <div class="text-sm font-medium text-slate-900 font-mono">{{ currentBill?.customer?.customerNo || '-' }}</div>
                    </div>
                    <div class="bg-white rounded-lg p-3 border border-slate-100">
                      <div class="text-xs text-slate-500 mb-1">客户名称</div>
                      <div class="text-sm font-semibold text-slate-900">{{ currentBill?.customer?.name || '-' }}</div>
                    </div>
                    <div class="bg-white rounded-lg p-3 border border-slate-100">
                      <div class="text-xs text-slate-500 mb-1">客户性别</div>
                      <div class="text-sm font-medium text-slate-900">{{ getGenderLabel(currentBill?.customer?.gender) }}</div>
                    </div>
                    <div class="bg-white rounded-lg p-3 border border-slate-100">
                      <div class="text-xs text-slate-500 mb-1">客户等级</div>
                      <div class="text-sm font-medium text-slate-900">{{ currentBill?.customer?.levelName || '-' }}</div>
                    </div>
                    <div class="bg-white rounded-lg p-3 border border-slate-100">
                      <div class="text-xs text-slate-500 mb-1">客户状态</div>
                      <div class="text-sm font-medium text-slate-900">{{ currentBill?.customer?.statusName || '-' }}</div>
                    </div>
                    <div class="bg-white rounded-lg p-3 border border-slate-100">
                      <div class="text-xs text-slate-500 mb-1">客户来源</div>
                      <div class="text-sm font-medium text-slate-900">{{ currentBill?.customer?.source || '-' }}</div>
                    </div>
                    <div class="bg-white rounded-lg p-3 border border-slate-100">
                      <div class="text-xs text-slate-500 mb-1">手机号</div>
                      <div class="text-sm font-medium text-slate-900">{{ currentBill?.customer?.phone || '-' }}</div>
                    </div>
                    <div class="bg-white rounded-lg p-3 border border-slate-100">
                      <div class="text-xs text-slate-500 mb-1">联系电话</div>
                      <div class="text-sm font-medium text-slate-900">{{ currentBill?.customer?.telPhone || '-' }}</div>
                    </div>
                    <div class="bg-white rounded-lg p-3 border border-slate-100">
                      <div class="text-xs text-slate-500 mb-1">邮箱</div>
                      <div class="text-sm font-medium text-slate-900">{{ currentBill?.customer?.email || '-' }}</div>
                    </div>
                    <div class="bg-white rounded-lg p-3 border border-slate-100">
                      <div class="text-xs text-slate-500 mb-1">身份证号</div>
                      <div class="text-sm font-medium text-slate-900">{{ currentBill?.customer?.idCard || '-' }}</div>
                    </div>
                    <div class="bg-white rounded-lg p-3 border border-slate-100">
                      <div class="text-xs text-slate-500 mb-1">引入人</div>
                      <div class="text-sm font-medium text-slate-900">{{ currentBill?.customer?.referrerName || '-' }}</div>
                    </div>
                    <div class="bg-white rounded-lg p-3 border border-slate-100">
                      <div class="text-xs text-slate-500 mb-1">所属人</div>
                      <div class="text-sm font-medium text-slate-900">{{ currentBill?.customer?.ownerUserName || '-' }}</div>
                    </div>
                    <div class="bg-white rounded-lg p-3 border border-slate-100">
                      <div class="text-xs text-slate-500 mb-1">所属部门</div>
                      <div class="text-sm font-medium text-slate-900">{{ currentBill?.customer?.ownerDeptName || '-' }}</div>
                    </div>
                  </div>
                </div>

                <div v-if="currentBill?.customer?.address || currentBill?.customer?.detailAddress" class="bg-slate-50/80 rounded-xl p-4">
                  <h4 class="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                    <span class="w-1.5 h-4 bg-slate-400 rounded-full" />
                    地址信息
                  </h4>
                  <div class="bg-white rounded-lg p-3 border border-slate-100">
                    <div class="text-sm text-slate-700">
                      {{ currentBill?.customer?.address || '' }}{{ currentBill?.customer?.detailAddress || '' }}
                    </div>
                  </div>
                </div>

                <div v-if="currentBill?.customer?.companies?.length" class="bg-slate-50/80 rounded-xl p-4">
                  <h4 class="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                    <span class="w-1.5 h-4 bg-blue-500 rounded-full" />
                    关联企业
                  </h4>
                  <div class="space-y-3">
                    <div v-for="company in currentBill.customer.companies" :key="company.id" class="bg-white rounded-lg p-4 border border-slate-100">
                      <div class="flex items-start justify-between mb-2">
                        <div class="text-sm font-semibold text-slate-900">{{ company.companyName }}</div>
                        <span
                          class="px-2 py-0.5 rounded text-xs font-medium"
                          :class="company.status === 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'"
                        >
                          {{ company.status === 0 ? '正常' : '停用' }}
                        </span>
                      </div>
                      <div class="grid grid-cols-2 gap-2 text-xs text-slate-500">
                        <div v-if="company.socialCreditCode">统一社会信用代码：{{ company.socialCreditCode }}</div>
                        <div v-if="company.legalPerson">法定代表人：{{ company.legalPerson }}</div>
                        <div v-if="company.registeredCapital">注册资本：{{ company.registeredCapital }}</div>
                        <div v-if="company.establishmentDate">成立日期：{{ company.establishmentDate }}</div>
                      </div>
                      <div v-if="company.registeredAddress" class="mt-2 text-xs text-slate-500">
                        注册地址：{{ company.registeredAddress }}
                      </div>
                      <div v-if="company.businessScope" class="mt-2 text-xs text-slate-500">
                        经营范围：{{ company.businessScope }}
                      </div>
                    </div>
                  </div>
                </div>

                <div v-if="currentBill?.customer?.tags || currentBill?.customer?.remark" class="bg-slate-50/80 rounded-xl p-4">
                  <h4 class="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                    <span class="w-1.5 h-4 bg-slate-400 rounded-full" />
                    标签与备注
                  </h4>
                  <div class="bg-white rounded-lg p-3 border border-slate-100">
                    <div v-if="currentBill?.customer?.tags" class="mb-2">
                      <div class="text-xs text-slate-500 mb-1">客户标签</div>
                      <div class="flex flex-wrap gap-1">
                        <span v-for="tag in String(currentBill.customer.tags).split(',')" :key="tag" class="px-2 py-0.5 bg-primary-50 text-primary-700 rounded text-xs">
                          {{ tag }}
                        </span>
                      </div>
                    </div>
                    <div v-if="currentBill?.customer?.remark">
                      <div class="text-xs text-slate-500 mb-1">客户备注</div>
                      <div class="text-sm text-slate-700 whitespace-pre-wrap">{{ currentBill.customer.remark }}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="flex-shrink-0 px-6 py-4 border-t border-slate-100 flex justify-between items-center bg-slate-50/50">
              <div class="flex items-center gap-3">
                <button
                  v-if="canExecuteCommission(currentBill)"
                  class="px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl text-sm font-medium shadow-sm hover:shadow-md hover:from-amber-600 hover:to-orange-600 active:scale-[0.98] transition-all flex items-center gap-1.5"
                  @click="handleExecuteCommission(currentBill)"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  执行分账
                </button>
                <div class="text-xs text-slate-400">
                  账单 ID: {{ currentBill?.id }} · 订单 ID: {{ currentBill?.orderId }} · 客户 ID: {{ currentBill?.customerId }}
                </div>
              </div>
              <button
                class="px-5 py-2.5 border border-slate-200 text-slate-700 rounded-xl text-sm font-medium hover:bg-white hover:border-slate-300 active:scale-[0.98] transition-all"
                @click="closeDetail"
              >
                关闭
              </button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- 预览分账弹窗 -->
    <Teleport to="body">
      <div v-if="showPreviewModal" class="fixed inset-0 z-50 overflow-y-auto" @keydown.esc="closePreviewModal">
        <div class="fixed inset-0 bg-slate-900/60 backdrop-blur-sm animate-fade-in" @click="closePreviewModal" />
        <div class="relative min-h-screen flex items-center justify-center p-4">
          <div class="relative bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[calc(100vh-2rem)] overflow-hidden animate-slide-up flex flex-col">
            <!-- 弹窗头部 -->
            <div class="flex-shrink-0 flex items-center justify-between px-6 py-4 bg-gradient-to-r from-amber-50 to-white border-b border-amber-100">
              <div class="flex items-center gap-3">
                <div class="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shadow-lg shadow-amber-500/20">
                  <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <div>
                  <h3 class="text-lg font-bold text-slate-900">分账预览</h3>
                  <p class="text-sm text-slate-500">请确认分账明细后再执行</p>
                </div>
              </div>
              <button class="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors" @click="closePreviewModal">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <!-- 加载状态 -->
            <div v-if="previewLoading" class="flex items-center justify-center py-16">
              <div class="animate-spin w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full" />
            </div>

            <!-- 预览内容 -->
            <div v-else-if="previewData" class="flex-1 min-h-0 overflow-y-auto px-6 py-5 space-y-5">
              <!-- 不可执行提示 -->
              <div v-if="!previewData.canExecute" class="bg-red-50 border border-red-200 rounded-xl p-4">
                <div class="flex items-start gap-3">
                  <svg class="w-5 h-5 text-red-500 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <div class="font-medium text-red-800">无法执行分账</div>
                    <div class="text-sm text-red-600 mt-1">{{ previewData.reason }}</div>
                  </div>
                </div>
              </div>

              <!-- 可执行时的预览内容 -->
              <template v-else>
                <!-- 分账概览 -->
                <div class="bg-amber-50 rounded-xl p-4 border border-amber-200">
                  <div class="grid grid-cols-1 sm:grid-cols-4 gap-3">
                    <div>
                      <div class="text-xs text-amber-600 font-medium">支付总额</div>
                      <div class="text-lg font-bold text-amber-900 mt-1">¥{{ formatAmount(previewData.grossPaidAmount) }}</div>
                    </div>
                    <div>
                      <div class="text-xs text-amber-600 font-medium">通道费</div>
                      <div class="text-lg font-bold text-amber-900 mt-1">¥{{ formatAmount(previewData.channelFeeAmount) }}</div>
                    </div>
                    <div>
                      <div class="text-xs text-amber-600 font-medium">净可分账金额</div>
                      <div class="text-2xl font-bold text-amber-900 mt-1">¥{{ formatAmount(getPreviewNetSeparableAmount(previewData)) }}</div>
                    </div>
                    <div>
                      <div class="text-xs text-amber-600 font-medium">引入人税费</div>
                      <div class="text-lg font-bold text-amber-900 mt-1">¥{{ formatAmount(previewData.taxSummary?.totalTaxAmount) }}</div>
                      <div class="text-xs text-amber-600 mt-1">固定税率 {{ formatRatePercent(previewData.taxSummary?.referrerTaxRate) }}</div>
                    </div>
                  </div>
                  <div class="mt-4 pt-4 border-t border-amber-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <div class="min-w-0">
                      <div class="text-xs text-amber-600 font-medium">匹配规则</div>
                      <div class="text-sm font-semibold text-amber-900 mt-1 truncate">{{ previewData.ruleName }}</div>
                    </div>
                    <div class="flex flex-wrap items-center gap-2">
                      <span class="px-2 py-0.5 bg-amber-100 text-amber-700 rounded text-xs">
                        {{ getRuleTypeLabel(previewData.ruleType) }}
                      </span>
                      <span class="text-xs text-amber-600">优先级 {{ previewData.priority }}</span>
                      <span v-if="previewData.taxSummary" class="px-2 py-0.5 bg-amber-100 text-amber-700 rounded text-xs">
                        税费归属：{{ getRoleCodeLabel(previewData.taxSummary.receiverRoleCode) }}
                      </span>
                    </div>
                  </div>
                  <div v-if="previewData.amountBaseDesc" class="text-xs text-amber-700 mt-3">
                    {{ previewData.amountBaseDesc }}
                  </div>
                </div>

                <!-- 分账明细 -->
                <div>
                  <h4 class="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                    <span class="w-1.5 h-4 bg-primary-500 rounded-full" />
                    分账明细
                  </h4>
                  <div class="space-y-2">
                    <div
                      v-for="(item, index) in previewData.details"
                      :key="index"
                      class="bg-slate-50 rounded-xl p-4 border border-slate-100"
                    >
                      <div class="flex items-center justify-between mb-2">
                        <div class="flex items-center gap-2">
                          <span class="px-2 py-1 bg-primary-100 text-primary-700 rounded-lg text-xs font-semibold">
                            {{ getRoleCodeLabel(item.roleCode) }}
                          </span>
                          <span class="text-sm font-medium text-slate-900">{{ item.roleInstanceName || '-' }}</span>
                        </div>
                        <div class="text-right">
                          <div class="text-lg font-bold text-slate-900">¥{{ formatAmount(getPreviewActualAmount(item)) }}</div>
                          <div class="text-xs text-slate-500">实际分账</div>
                          <div class="text-xs text-slate-500">
                            {{ getCalculateTypeLabel(item.calculateType) }}
                            <span v-if="item.calculateType === 'RATIO'">{{ item.ratio }}%</span>
                            <span v-else-if="item.calculateType === 'FIXED'">¥{{ formatAmount(item.ratio) }}</span>
                          </div>
                        </div>
                      </div>
                      <div class="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-3">
                        <div class="bg-white rounded-lg px-3 py-2 border border-slate-100">
                          <div class="text-xs text-slate-500">规则金额</div>
                          <div class="text-sm font-semibold text-slate-900 mt-1">¥{{ formatAmount(item.amount) }}</div>
                        </div>
                        <div class="bg-white rounded-lg px-3 py-2 border border-slate-100">
                          <div class="text-xs text-slate-500">{{ getTaxDirectionLabel(item.taxDirection) }}</div>
                          <div class="text-sm font-semibold mt-1" :class="getTaxAmountClass(item.taxDirection)">
                            ¥{{ formatAmount(item.taxAmount) }}
                          </div>
                        </div>
                        <div class="bg-white rounded-lg px-3 py-2 border border-slate-100">
                          <div class="text-xs text-slate-500">税率</div>
                          <div class="text-sm font-semibold text-slate-900 mt-1">{{ formatRatePercent(item.taxRate) }}</div>
                        </div>
                      </div>
                      <div v-if="item.taxDescription" class="text-xs text-slate-500 mt-2">
                        {{ item.taxDescription }}
                      </div>
                      <div v-if="item.description" class="text-xs text-slate-500 mt-2 pt-2 border-t border-slate-100">
                        {{ item.description }}
                      </div>
                    </div>
                  </div>
                </div>

                <!-- 支付通道费明细 -->
                <div v-if="previewData.paymentItems?.length">
                  <h4 class="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                    <span class="w-1.5 h-4 bg-amber-500 rounded-full" />
                    支付与通道费
                  </h4>
                  <div class="rounded-xl border border-slate-200 overflow-hidden bg-white">
                    <div
                      v-for="(item, index) in previewData.paymentItems"
                      :key="item.paymentOrderId || item.paymentOrderNo || index"
                      class="p-4 border-b border-slate-100 last:border-b-0"
                    >
                      <div class="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                        <div class="min-w-0">
                          <div class="text-sm font-semibold text-slate-900 truncate">{{ item.paymentOrderNo || '-' }}</div>
                          <div class="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                            <span>{{ getPayProductCodeLabel(item.payProductCode) }}</span>
                            <span>{{ getFeeConfigText(item) }}</span>
                            <span v-if="item.feeRoundingMode">{{ getFeeRoundingModeLabel(item.feeRoundingMode) }}</span>
                          </div>
                        </div>
                        <span class="shrink-0 px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-xs">
                          {{ getFeeSourceLabel(item.feeSource) }}
                        </span>
                      </div>
                      <div class="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2">
                        <div class="bg-slate-50 rounded-lg px-3 py-2">
                          <div class="text-xs text-slate-500">支付金额</div>
                          <div class="text-sm font-semibold text-slate-900 mt-1">¥{{ formatAmount(item.paidAmount) }}</div>
                        </div>
                        <div class="bg-slate-50 rounded-lg px-3 py-2">
                          <div class="text-xs text-slate-500">通道费</div>
                          <div class="text-sm font-semibold text-slate-900 mt-1">¥{{ formatAmount(item.channelFeeAmount) }}</div>
                        </div>
                        <div class="bg-slate-50 rounded-lg px-3 py-2">
                          <div class="text-xs text-slate-500">净可分账</div>
                          <div class="text-sm font-semibold text-slate-900 mt-1">¥{{ formatAmount(item.netSeparableAmount) }}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </div>

            <!-- 弹窗底部 -->
            <div class="flex-shrink-0 px-6 py-4 border-t border-slate-100 flex justify-end gap-3 bg-slate-50/50">
              <button
                class="px-5 py-2.5 border border-slate-200 text-slate-700 rounded-xl text-sm font-medium hover:bg-white hover:border-slate-300 active:scale-[0.98] transition-all"
                @click="closePreviewModal"
              >
                取消
              </button>
              <button
                v-if="previewData?.canExecute"
                class="px-5 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl text-sm font-medium shadow-sm hover:shadow-md hover:from-amber-600 hover:to-orange-600 active:scale-[0.98] transition-all flex items-center gap-1.5"
                @click="confirmExecuteCommission"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                </svg>
                确认执行
              </button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>
  </ListPageShell>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { ElMessage } from 'element-plus'
import { getBillingPage, executeCommission, getBillingDetail, previewCommission, getBillingPayments, getBillingCommissions } from '@/api/billing'
import { createOrGetCashierByBilling } from '@/api/cashier'
import AppPagination from '@/components/AppPagination.vue'
import ListPageShell from '@/components/ListPageShell.vue'
import PageFilterBar from '@/components/PageFilterBar.vue'

// 筛选表单
const searchForm = reactive({
  keyword: '',
  statusType: 2, // 默认显示待分账
  customerId: '',
  orderId: '',
  dueDateRange: []
})

// 支付 / 分账状态快捷筛选（与后端 statusType 含义保持一致）
const statusTypeTabs = [
  { value: '', label: '全部' },
  { value: 1, label: '未支付' },
  { value: 4, label: '逾期' },
  { value: 2, label: '待分账' },
  { value: 3, label: '已分账' }
]

// 点击状态标签筛选
const handleStatusTabClick = (value) => {
  searchForm.statusType = value
  handleSearch()
}

const loading = ref(false)
const billingList = ref([])

const pagination = reactive({
  current: 1,
  size: 9,
  total: 0,
  pages: 0
})

// 详情弹窗
const showDetailModal = ref(false)
const currentBill = ref(null)
const activeDetailTab = ref('basic')
const detailPayments = ref([])
const detailCommissions = ref([])
const detailLoading = ref(false)

// 预览分账弹窗
const showPreviewModal = ref(false)
const previewData = ref(null)
const previewLoading = ref(false)
const creatingCashierBillingId = ref(null)

// 详情弹窗 Tab 配置
const detailTabs = [
  { key: 'basic', label: '账单信息' },
  { key: 'order', label: '订单与产品' },
  { key: 'customer', label: '客户信息' }
]

// 支付渠道标签
const getPayChannelLabel = (channel) => {
  const map = {
    'WECHAT': '微信',
    'ALIPAY': '支付宝',
    'BANK_TRANSFER': '银行转账',
    'CASH': '现金',
    'OTHER': '其他'
  }
  return map[channel] || channel || '-'
}

// 性别标签
const getGenderLabel = (gender) => {
  const map = { 0: '男', 1: '女', 2: '未知' }
  return map[gender] ?? '-'
}

// 订单状态标签
const getOrderStatusLabel = (status) => {
  const map = {
    0: '待确认',
    1: '执行中',
    2: '已完成',
    3: '已取消',
    4: '暂停'
  }
  return map[status] ?? '-'
}

// 金额格式化
const formatAmount = (value) => {
  const num = Number(value)
  const safeNum = Number.isFinite(num) ? num : 0
  return safeNum.toLocaleString('zh-CN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })
}

const getPreviewNetSeparableAmount = (data) => data?.netSeparableAmount ?? data?.totalAmount

const getPreviewActualAmount = (item) => item?.actualAmount ?? item?.amount

const formatRatePercent = (rate) => {
  const num = Number(rate)
  if (!Number.isFinite(num)) return '-'
  return `${(num * 100).toFixed(2).replace(/\.?0+$/, '')}%`
}

const getTaxDirectionLabel = (direction) => {
  const map = {
    DEDUCT: '扣税',
    COLLECT: '代收税费',
    NONE: '税费'
  }
  return map[direction] || '税费'
}

const getTaxAmountClass = (direction) => {
  const map = {
    DEDUCT: 'text-red-600',
    COLLECT: 'text-emerald-600',
    NONE: 'text-slate-700'
  }
  return map[direction] || 'text-slate-700'
}

// 回款进度百分比
const getPaidPercent = (bill) => {
  const total = Number(bill?.amount) || 0
  const paid = Number(bill?.paidAmount) || 0
  if (total <= 0) return '0%'
  const ratio = Math.max(0, Math.min(1, paid / total))
  return `${(ratio * 100).toFixed(0)}%`
}

// 账单状态：0待收 1已收 2逾期 3坏账
const getBillingStatusLabel = (status) => {
  const map = {
    0: '待收',
    1: '已收',
    2: '逾期',
    3: '坏账'
  }
  return map[status] ?? '-'
}

const getBillingStatusClass = (status) => {
  const map = {
    0: 'bg-blue-100 text-blue-700',
    1: 'bg-emerald-100 text-emerald-700',
    2: 'bg-red-100 text-red-700',
    3: 'bg-slate-100 text-slate-700'
  }
  return map[status] || 'bg-slate-100 text-slate-700'
}

// 根据支付状态 + 账单状态合成更易懂的收款状态
const getPaymentStatusLabel = (bill) => {
  if (!bill) return '-'
  const { status, paymentStatus, commissionStatus } = bill

  // 坏账状态
  if (status === 3) return '坏账'

  // 逾期未支付：status=2 且 paymentStatus=UNPAID
  if (status === 2 && paymentStatus === 'UNPAID') return '逾期未支付'

  // 处理空值或未知值
  const ps = paymentStatus?.toUpperCase?.() || ''

  if (ps === 'UNPAID') return '未支付'
  if (ps === 'PARTIALLY_PAID') return '部分支付'

  if (ps === 'PAID') {
    // 已支付：根据分账状态细分
    if (commissionStatus === 2) return '已支付已分账'
    if (commissionStatus === 1) return '已支付部分分账'
    return '已支付'
  }

  // 如果 paymentStatus 为空但 status 已知，推断状态
  if (status === 0) return '未支付'
  if (status === 1) return '已支付'
  if (status === 2) return '逾期未支付'

  return '-'
}

const getPaymentStatusClass = (bill) => {
  const label = getPaymentStatusLabel(bill)
  const map = {
    未支付: 'bg-slate-100 text-slate-600',
    '逾期未支付': 'bg-red-100 text-red-700',
    部分支付: 'bg-amber-100 text-amber-700',
    已支付: 'bg-emerald-100 text-emerald-700',
    已支付已分账: 'bg-emerald-100 text-emerald-700',
    已支付部分分账: 'bg-indigo-100 text-indigo-700',
    坏账: 'bg-red-100 text-red-700'
  }
  return map[label] || 'bg-slate-100 text-slate-600'
}

const getPaymentStatusDotClass = (bill) => {
  const label = getPaymentStatusLabel(bill)
  const map = {
    未支付: 'bg-slate-400',
    '逾期未支付': 'bg-red-500',
    部分支付: 'bg-amber-500',
    已支付: 'bg-emerald-500',
    已支付已分账: 'bg-emerald-500',
    已支付部分分账: 'bg-indigo-500',
    坏账: 'bg-red-600'
  }
  return map[label] || 'bg-slate-400'
}

// 分账状态：0未分账 1部分分账 2已分账
const getCommissionStatusLabel = (status) => {
  const map = {
    0: '未分账',
    1: '部分分账',
    2: '已分账'
  }
  return map[status] ?? '-'
}

const getCommissionStatusClass = (status) => {
  const map = {
    0: 'bg-slate-100 text-slate-600',
    1: 'bg-indigo-100 text-indigo-700',
    2: 'bg-emerald-100 text-emerald-700'
  }
  return map[status] || 'bg-slate-100 text-slate-600'
}

// 规则类型标签
const getRuleTypeLabel = (type) => {
  const map = {
    'GLOBAL': '全局规则',
    'FRANCHISEE': '加盟商规则',
    'PRODUCT': '产品规则'
  }
  return map[type] || type || '-'
}

// 计算类型标签
const getCalculateTypeLabel = (type) => {
  const map = {
    'RATIO': '按比例',
    'FIXED': '固定金额',
    'LADDER': '阶梯计算',
    'MINIMUM': '保底',
    'REMAINDER': '余量分配'
  }
  return map[type] || type || '-'
}

// 角色编码标签
const getRoleCodeLabel = (code) => {
  const map = {
    'REFERRER': '引入人',
    'FRANCHISEE': '加盟商',
    'PLATFORM': '平台'
  }
  return map[code] || code || '-'
}

const getPayProductCodeLabel = (code) => {
  const map = {
    'PRO_SCAN': '扫码支付',
    'COUNTER': '柜台支付',
    'EBANK': '网银支付',
    'BANK_TRANSFER': '银行转账'
  }
  return map[code] || code || '-'
}

const getFeeSourceLabel = (source) => {
  const map = {
    'MANUAL': '手工配置',
    'LAKALA': '拉卡拉同步',
    'DEFAULT': '默认配置',
    'LAKALA_FEE_CALC': '拉卡拉试算',
    'LAKALA_MERCHANT_BILLING_QUERY': '商户计费查询'
  }
  return map[source] || source || '-'
}

const getFeeRoundingModeLabel = (mode) => {
  const map = {
    'CEILING': '向上取整',
    'HALF_UP': '四舍五入'
  }
  return map[mode] || mode || '-'
}

const formatFeeRate = (rate) => {
  const num = Number(rate)
  if (!Number.isFinite(num)) return '-'
  return `${(num * 100).toFixed(4).replace(/\.?0+$/, '')}%`
}

const getFeeConfigText = (item) => {
  if (!item) return '-'
  if (item.feeType === 'RATE') return `比例费率 ${formatFeeRate(item.feeRate)}`
  if (item.feeType === 'FIXED') return `固定通道费 ¥${formatAmount(item.fixedFeeAmount)}`
  return item.feeType || '-'
}

// 支付状态标签
const getPaymentRecordStatusLabel = (status) => {
  const map = {
    'PENDING': '待支付',
    'SUCCESS': '支付成功',
    'FAILED': '支付失败',
    'REFUNDED': '已退款'
  }
  return map[status] || status || '-'
}

const getPaymentRecordStatusClass = (status) => {
  const map = {
    'PENDING': 'bg-amber-100 text-amber-700',
    'SUCCESS': 'bg-emerald-100 text-emerald-700',
    'FAILED': 'bg-red-100 text-red-700',
    'REFUNDED': 'bg-slate-100 text-slate-700'
  }
  return map[status] || 'bg-slate-100 text-slate-600'
}

// 支付渠道标签
const getChannelTypeLabel = (channel) => {
  const map = {
    'WECHAT': '微信支付',
    'ALIPAY': '支付宝',
    'BANK_CARD': '银行卡',
    'BANK_TRANSFER': '银行转账',
    'CASH': '现金',
    'OTHER': '其他'
  }
  return map[channel] || channel || '-'
}

// 分账结算状态标签（主状态）
const getSettlementStatusLabel = (status) => {
  const map = {
    0: '待分账',
    1: '分账中',
    2: '分账完成',
    3: '分账失败',
    4: '已取消'
  }
  return map[status] ?? '-'
}

const getSettlementStatusClass = (status) => {
  const map = {
    0: 'bg-amber-100 text-amber-700',
    1: 'bg-blue-100 text-blue-700',
    2: 'bg-emerald-100 text-emerald-700',
    3: 'bg-red-100 text-red-700',
    4: 'bg-slate-100 text-slate-600'
  }
  return map[status] || 'bg-slate-100 text-slate-600'
}

// 分账明细状态标签
const getCommissionItemStatusLabel = (status) => {
  const map = {
    0: '待结算',
    1: '已结算',
    2: '已取消'
  }
  return map[status] ?? '-'
}

const getCommissionItemStatusClass = (status) => {
  const map = {
    0: 'bg-amber-100 text-amber-700',
    1: 'bg-emerald-100 text-emerald-700',
    2: 'bg-slate-100 text-slate-600'
  }
  return map[status] || 'bg-slate-100 text-slate-600'
}

// 加载账单列表
const fetchBillingList = async () => {
  loading.value = true
  try {
    const params = {
      statusType: searchForm.statusType !== '' ? Number(searchForm.statusType) : undefined,
      customerId: searchForm.customerId ? Number(searchForm.customerId) : undefined,
      orderId: searchForm.orderId ? Number(searchForm.orderId) : undefined,
      keyword: searchForm.keyword || undefined,
      dueDateStart:
        searchForm.dueDateRange && searchForm.dueDateRange.length > 0
          ? searchForm.dueDateRange[0]
          : undefined,
      dueDateEnd:
        searchForm.dueDateRange && searchForm.dueDateRange.length > 1
          ? searchForm.dueDateRange[1]
          : undefined,
      page: pagination.current,
      size: pagination.size
    }

    const data = await getBillingPage(params)
    billingList.value = data.records || []
    pagination.current = data.current
    pagination.size = data.size
    pagination.total = data.total
    pagination.pages = data.pages
  } catch (error) {
    ElMessage.error(error.message || '获取分账账单列表失败')
  } finally {
    loading.value = false
  }
}

const handleSearch = () => {
  pagination.current = 1
  fetchBillingList()
}

const resetSearch = () => {
  searchForm.keyword = ''
  searchForm.statusType = 2 // 重置回待分账
  searchForm.customerId = ''
  searchForm.orderId = ''
  searchForm.dueDateRange = []
  pagination.current = 1
  fetchBillingList()
}

const handlePageChange = (page) => {
  if (page < 1 || (pagination.pages && page > pagination.pages)) return
  pagination.current = page
  fetchBillingList()
}

const handlePageSizeChange = (size) => {
  pagination.size = size
  pagination.current = 1
  fetchBillingList()
}

// 判断是否可以执行分账：status=1（已支付待分账）且 commissionStatus=0或1
const canExecuteCommission = (bill) => {
  if (!bill) return false
  return bill.status === 1 && (bill.commissionStatus === 0 || bill.commissionStatus === 1)
}

// 执行分账
const handleExecuteCommission = async (bill) => {
  previewLoading.value = true
  try {
    previewData.value = await previewCommission(bill.id)
    showPreviewModal.value = true
  } catch (error) {
    ElMessage.error(error.message || '获取分账预览失败')
  } finally {
    previewLoading.value = false
  }
}

// 确认执行分账
const confirmExecuteCommission = async () => {
  if (!previewData.value || !previewData.value.canExecute) return
  try {
    await executeCommission({ billingId: previewData.value.billingId })
    ElMessage.success('分账执行成功')
    showPreviewModal.value = false
    previewData.value = null
    fetchBillingList()
  } catch (error) {
    ElMessage.error(error.message || '分账执行失败')
  }
}

// 关闭预览弹窗
const closePreviewModal = () => {
  showPreviewModal.value = false
  previewData.value = null
}

const openCashierPage = async (bill) => {
  if (!bill?.id) return
  creatingCashierBillingId.value = bill.id
  try {
    const created = await createOrGetCashierByBilling(Number(bill.id))
    const cashierNo = created?.cashierNo || ''
    if (!cashierNo) {
      ElMessage.error('收银台创建失败，请稍后重试')
      return
    }
    bill.cashierNo = cashierNo
    const targetUrl = `${window.location.origin}/cashier/${cashierNo}`
    window.open(targetUrl, '_blank')
  } catch (error) {
    ElMessage.error(error?.message || '创建收银台失败，请稍后重试')
  } finally {
    creatingCashierBillingId.value = null
  }
}

// 详情
const showDetail = async (bill) => {
  detailLoading.value = true
  try {
    currentBill.value = await getBillingDetail(bill.id)
    // 并行加载支付记录和分账记录
    const [payments, commissions] = await Promise.all([
      getBillingPayments(bill.id).catch(() => []),
      getBillingCommissions(bill.id).catch(() => [])
    ])
    detailPayments.value = payments || []
    detailCommissions.value = commissions || []
  } catch (error) {
    ElMessage.error('获取账单详情失败')
    return
  } finally {
    detailLoading.value = false
  }
  showDetailModal.value = true
  document.body.style.overflow = 'hidden'
}

const closeDetail = () => {
  showDetailModal.value = false
  currentBill.value = null
  detailPayments.value = []
  detailCommissions.value = []
  document.body.style.overflow = ''
}

// 键盘事件
const handleKeydown = (e) => {
  if (e.key === 'Escape' && showDetailModal.value) {
    closeDetail()
  }
}

onMounted(() => {
  fetchBillingList()
  window.addEventListener('keydown', handleKeydown)
})
</script>

<style scoped>
/* 弹窗动画 */
@keyframes fade-in {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slide-up {
  from {
    opacity: 0;
    transform: translateY(20px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.animate-fade-in {
  animation: fade-in 200ms ease-out;
}

.animate-slide-up {
  animation: slide-up 300ms cubic-bezier(0.16, 1, 0.3, 1);
}

/* 卡片悬停效果增强 */
.billing-card:hover .detail-btn {
  opacity: 1;
}

/* 滚动条美化 */
.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: transparent;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* 进度条渐变动画 */
.progress-bar-animated {
  background: linear-gradient(
    90deg,
    from-emerald-400,
    via-emerald-500,
    to-emerald-400
  );
  background-size: 200% 100%;
  animation: shimmer 2s infinite;
}

@keyframes shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}
</style>
